import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  category: string;
  tags: string[];
  authorName: string;
  publishDate: string;
  readingTime: string;
}

interface ArticleCardProps {
  article: Article;
  variant?: 'featured' | 'standard' | 'compact' | 'sidebar';
}

// Featured Article Card (Hero/Main)
const FeaturedCard = styled(Link)`
  display: block;
  position: relative;
  height: 500px;
  border-radius: var(--radius-2xl);
  overflow: hidden;
  text-decoration: none;
  box-shadow: var(--shadow-xl);
  transition: all var(--transition-normal);
  background: var(--carbon-gradient);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-racing);
  }
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
  
  ${FeaturedCard}:hover & {
    transform: scale(1.05);
  }
`;

const FeaturedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 10, 10, 0.9) 0%,
    rgba(10, 10, 10, 0.4) 50%,
    transparent 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: var(--space-8);
  
  @media (max-width: 768px) {
    padding: var(--space-6);
  }
`;

const FeaturedCategory = styled.span`
  background: var(--racing-gradient);
  color: var(--white);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-4);
  align-self: flex-start;
  box-shadow: var(--shadow-racing);
`;

const FeaturedTitle = styled.h2`
  color: var(--white);
  font-size: var(--text-3xl);
  font-weight: var(--font-black);
  line-height: var(--leading-tight);
  margin-bottom: var(--space-4);
  
  @media (max-width: 768px) {
    font-size: var(--text-2xl);
  }
`;

const FeaturedExcerpt = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
  
  @media (max-width: 768px) {
    font-size: var(--text-base);
  }
`;

const FeaturedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  color: var(--chrome-silver);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
`;

// Standard Article Card
const StandardCard = styled(Link)`
  display: block;
  background: var(--white);
  border-radius: var(--radius-xl);
  overflow: hidden;
  text-decoration: none;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-xl);
  }
`;

const StandardImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  transition: transform var(--transition-normal);
  
  ${StandardCard}:hover & {
    transform: scale(1.02);
  }
`;

const StandardContent = styled.div`
  padding: var(--space-6);
`;

const StandardCategory = styled.span`
  background: var(--electric-gradient);
  color: var(--white);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: var(--font-bold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-3);
  display: inline-block;
`;

const StandardTitle = styled.h3`
  color: var(--carbon-black);
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  line-height: var(--leading-tight);
  margin-bottom: var(--space-3);
  
  ${StandardCard}:hover & {
    color: var(--racing-red);
  }
`;

const StandardExcerpt = styled.p`
  color: var(--dark-gray);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StandardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--medium-gray);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
`;

// Compact Article Card
const CompactCard = styled(Link)`
  display: flex;
  gap: var(--space-4);
  background: var(--white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-fast);
  padding: var(--space-4);
  
  &:hover {
    box-shadow: var(--shadow-md);
    background: var(--light-gray);
  }
`;

const CompactImage = styled.img`
  width: 100px;
  height: 80px;
  object-fit: cover;
  border-radius: var(--radius-md);
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 80px;
    height: 60px;
  }
`;

const CompactContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactCategory = styled.span`
  background: var(--turbo-orange);
  color: var(--white);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: var(--space-2);
  display: inline-block;
`;

const CompactTitle = styled.h4`
  color: var(--carbon-black);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  line-height: var(--leading-snug);
  margin-bottom: var(--space-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  ${CompactCard}:hover & {
    color: var(--racing-red);
  }
`;

const CompactMeta = styled.div`
  color: var(--medium-gray);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
`;

// Sidebar Article Card
const SidebarCard = styled(Link)`
  display: block;
  background: var(--white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-fast);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const SidebarImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
`;

const SidebarContent = styled.div`
  padding: var(--space-4);
`;

const SidebarCategory = styled.span`
  background: var(--neon-green);
  color: var(--carbon-black);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-bold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: var(--space-2);
  display: inline-block;
`;

const SidebarTitle = styled.h5`
  color: var(--carbon-black);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  line-height: var(--leading-snug);
  margin-bottom: var(--space-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  ${SidebarCard}:hover & {
    color: var(--racing-red);
  }
`;

const SidebarMeta = styled.div`
  color: var(--medium-gray);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
`;

const ArticleCard: React.FC<ArticleCardProps> = ({ article, variant = 'standard' }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (variant === 'featured') {
    return (
      <FeaturedCard to={`/article/${article.id}`}>
        <FeaturedImage src={article.image} alt={article.title} />
        <FeaturedOverlay>
          <FeaturedCategory>{article.category}</FeaturedCategory>
          <FeaturedTitle>{article.title}</FeaturedTitle>
          <FeaturedExcerpt>{article.excerpt}</FeaturedExcerpt>
          <FeaturedMeta>
            <span>{article.authorName}</span>
            <span>•</span>
            <span>{formatDate(article.publishDate)}</span>
            <span>•</span>
            <span>{article.readingTime}</span>
          </FeaturedMeta>
        </FeaturedOverlay>
      </FeaturedCard>
    );
  }

  if (variant === 'compact') {
    return (
      <CompactCard to={`/article/${article.id}`}>
        <CompactImage src={article.image} alt={article.title} />
        <CompactContent>
          <CompactCategory>{article.category}</CompactCategory>
          <CompactTitle>{article.title}</CompactTitle>
          <CompactMeta>
            {formatDate(article.publishDate)} • {article.readingTime}
          </CompactMeta>
        </CompactContent>
      </CompactCard>
    );
  }

  if (variant === 'sidebar') {
    return (
      <SidebarCard to={`/article/${article.id}`}>
        <SidebarImage src={article.image} alt={article.title} />
        <SidebarContent>
          <SidebarCategory>{article.category}</SidebarCategory>
          <SidebarTitle>{article.title}</SidebarTitle>
          <SidebarMeta>
            {formatDate(article.publishDate)}
          </SidebarMeta>
        </SidebarContent>
      </SidebarCard>
    );
  }

  // Standard variant
  return (
    <StandardCard to={`/article/${article.id}`}>
      <StandardImage src={article.image} alt={article.title} />
      <StandardContent>
        <StandardCategory>{article.category}</StandardCategory>
        <StandardTitle>{article.title}</StandardTitle>
        <StandardExcerpt>{article.excerpt}</StandardExcerpt>
        <StandardMeta>
          <span>{article.authorName}</span>
          <span>{formatDate(article.publishDate)} • {article.readingTime}</span>
        </StandardMeta>
      </StandardContent>
    </StandardCard>
  );
};

export default ArticleCard;
